import React, { useState, useEffect, useRef } from "react";
import { Button, Card, Input, Alert, Spin, Typography, Space, Checkbox, Tag, Tooltip } from "antd";
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LockOutlined } from "@ant-design/icons";
import { satoshisToAmount } from "../utils";
import { useUtxoSelection } from "../contexts/UtxoSelectionContext";
import { useUtxos } from "../hooks/useUtxos";

const { Text, Link } = Typography;

interface UtxosListCardProps {
  defaultAddress?: string;
}

export function UtxosListCard({ defaultAddress = "" }: UtxosListCardProps) {
  const [address, setAddress] = useState(defaultAddress);
  const [fetchStatus, setFetchStatus] = useState(false);
  const isUserModifiedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const { toggleUtxo, isUtxoSelected } = useUtxoSelection();

  const {
    utxos,
    loading,
    error,
    fetchUtxos,
    fetchUtxoStatus,
    refreshUtxos,
    clearError,
  } = useUtxos({
    fetchStatus: fetchStatus,
    onError: (err) => {
      console.error("UTXO fetch error:", err);
    },
  });

  // Initialiser avec l'adresse par défaut UNIQUEMENT au premier montage
  useEffect(() => {
    if (!hasInitializedRef.current && defaultAddress && defaultAddress.trim() !== "") {
      setAddress(defaultAddress);
      hasInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load when address is available
  useEffect(() => {
    if (address && address.trim() && !isUserModifiedRef.current) {
      const trimmedAddress = address.trim();
      const bitcoinAddressRegex = /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/;
      if (bitcoinAddressRegex.test(trimmedAddress)) {
        fetchUtxos(trimmedAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchUtxos = async () => {
    if (!address.trim()) {
      return;
    }
    await fetchUtxos(address.trim());
  };

  const handleRefreshStatus = async () => {
    if (!address.trim() || utxos.length === 0) {
      return;
    }

    setFetchStatus(true);
    clearError(); // Clear any previous errors
    
    try {
      // Fetch status for all UTXOs
      for (const utxo of utxos) {
        if (!utxo.status) {
          try {
            await fetchUtxoStatus(utxo.txid, utxo.vout, address.trim());
          } catch (err: any) {
            // Log individual UTXO errors but continue with others
            console.warn(`Failed to fetch status for UTXO ${utxo.txid}:${utxo.vout}:`, err);
          }
        }
      }
    } catch (err: any) {
      // Format error message properly
      const errorMessage = err?.message || 
                          (typeof err === 'string' ? err : JSON.stringify(err)) || 
                          'Failed to refresh UTXO status';
      console.error('Error refreshing UTXO status:', errorMessage);
    } finally {
      setFetchStatus(false);
    }
  };

  // Séparer les UTXOs par catégorie
  // 1. UTXOs avec inscriptions (isInscription: true ou inscriptions.length > 0)
  const inscriptionUtxos = utxos.filter((utxo) => {
    const hasInscriptions = (utxo as any).isInscription === true || 
                           ((utxo as any).inscriptions && (utxo as any).inscriptions.length > 0);
    return hasInscriptions && !utxo.isLocked;
  });
  
  // 2. UTXOs sans inscriptions (non inscriptions, non locked)
  const nonInscriptionUtxos = utxos.filter((utxo) => {
    const hasInscriptions = (utxo as any).isInscription === true || 
                           ((utxo as any).inscriptions && (utxo as any).inscriptions.length > 0);
    return !hasInscriptions && !utxo.isSpent && !utxo.isLocked;
  });
  
  // 3. UTXOs locked par le marché
  const lockedUtxos = utxos.filter((utxo) => utxo.isLocked === true);
  
  // 4. UTXOs spent (pour historique)
  const spentUtxos = utxos.filter((utxo) => utxo.isSpent === true && !utxo.isLocked);
  
  // 5. Status unknown
  const unknownStatusUtxos = utxos.filter((utxo) => utxo.isSpent === undefined && utxo.isLocked === undefined);

  // Calculer les totaux (avec vérification de sécurité)
  const totalInscriptions = inscriptionUtxos.reduce((sum, utxo) => {
    const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
    return sum + satoshi;
  }, 0);
  const totalNonInscriptions = nonInscriptionUtxos.reduce((sum, utxo) => {
    const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
    return sum + satoshi;
  }, 0);
  const totalLocked = lockedUtxos.reduce((sum, utxo) => {
    const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
    return sum + satoshi;
  }, 0);
  const totalSpent = spentUtxos.reduce((sum, utxo) => {
    const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
    return sum + satoshi;
  }, 0);
  const totalUnknown = unknownStatusUtxos.reduce((sum, utxo) => {
    const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
    return sum + satoshi;
  }, 0);

  const formatOutpointDisplay = (txid: string, vout: number): { txid: string; vout: string } => {
    return {
      txid: txid || "",
      vout: vout.toString() || "0",
    };
  };

  const renderUtxoItem = (
    utxo: typeof utxos[0],
    idx: number,
    showStatus: boolean = true
  ) => {
    // Safety check: ensure satoshi is a valid number
    const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) 
      ? utxo.satoshi 
      : 0;
    
    const { txid, vout } = formatOutpointDisplay(utxo.txid, utxo.vout);
    const btcAmount = satoshisToAmount(satoshi);
    const outpoint = `${utxo.txid}:${utxo.vout}`;
    const isSelected = isUtxoSelected(outpoint);
    const isSpent = utxo.isSpent === true && !utxo.isLocked;
    const isLocked = utxo.isLocked === true;
    const statusUnknown = utxo.isSpent === undefined && utxo.isLocked === undefined;

    return (
      <div
        key={idx}
        style={{
          marginBottom: 8,
          padding: "8px",
          backgroundColor: isSelected
            ? "rgba(24, 144, 255, 0.1)"
            : isLocked
            ? "rgba(255, 193, 7, 0.05)"
            : isSpent
            ? "rgba(255, 77, 79, 0.05)"
            : "var(--bg-card)",
          borderRadius: "6px",
          fontFamily: "monospace",
          fontSize: "13px",
          border: isSelected
            ? "1px solid #1890ff"
            : isLocked
            ? "1px solid rgba(255, 193, 7, 0.3)"
            : isSpent
            ? "1px solid rgba(255, 77, 79, 0.3)"
            : "1px solid transparent",
          cursor: (isSpent || isLocked) ? "not-allowed" : "pointer",
          opacity: (isSpent || isLocked) ? 0.6 : 1,
        }}
        onClick={() => {
          if (!isSpent && !isLocked) {
            const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
            toggleUtxo({
              outpoint,
              value: satoshi,
              address: address.trim(),
              scriptPk: utxo.scriptPk || undefined,
            });
          }
        }}
      >
        <Space>
          {!isSpent && !isLocked && (
            <Checkbox
              checked={isSelected}
              onChange={() => {
                const satoshi = typeof utxo.satoshi === 'number' && !isNaN(utxo.satoshi) ? utxo.satoshi : 0;
                toggleUtxo({
                  outpoint,
                  value: satoshi,
                  address: address.trim(),
                  scriptPk: utxo.scriptPk || undefined,
                });
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {showStatus && (
            <>
              {isLocked ? (
                <Tooltip title="This UTXO is locked by the market">
                  <Tag color="orange" icon={<LockOutlined />}>
                    Locked UTXO (By market)
                  </Tag>
                </Tooltip>
              ) : isSpent ? (
                <Tooltip title="This UTXO has been spent">
                  <Tag color="red" icon={<CloseCircleOutlined />}>
                    Spent
                  </Tag>
                </Tooltip>
              ) : statusUnknown ? (
                <Tooltip title="Status unknown - click refresh to check">
                  <Tag color="default">Unknown</Tag>
                </Tooltip>
              ) : (utxo as any).isInscription || ((utxo as any).inscriptions && (utxo as any).inscriptions.length > 0) ? (
                <Tooltip title="This UTXO contains an inscription">
                  <Tag color="orange" icon={<LockOutlined />}>
                    Inscription
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip title="This UTXO is available">
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Available
                  </Tag>
                </Tooltip>
              )}
            </>
          )}
          <Link
            href={`https://opool.space/tx/${txid}:${vout}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {txid}:{vout}
          </Link>{" "}
          <Text strong>{satoshi.toLocaleString()} sats</Text>{" "}
          <Text type="secondary">(=)</Text>{" "}
          <Text strong>{btcAmount} BTC</Text>
        </Space>
        {(utxo as any).inscriptions && (utxo as any).inscriptions.length > 0 && (
          <div style={{ marginTop: 6, fontSize: "12px", paddingLeft: 24 }}>
            <Text type="secondary">Inscriptions: </Text>
            {(utxo as any).inscriptions.map((inscription: any, i: number) => (
              <Link
                key={i}
                href={`https://ordiscan.com/inscription/${inscription.inscriptionId}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 4 }}
                onClick={(e) => e.stopPropagation()}
              >
                {inscription.inscriptionId.slice(0, 8)}...{inscription.inscriptionId.slice(-8)}
              </Link>
            ))}
          </div>
        )}
        {utxo.scriptPk && (
          <div style={{ marginTop: 4, paddingLeft: 24 }}>
            <Tooltip title={`ScriptPK: ${utxo.scriptPk}`}>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                [scriptPk]
              </Text>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card
      size="small"
      title="UTXOs List"
      className="utxos-list-card-fluo"
      style={{
        margin: 10,
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
      }}
      extra={
        utxos.length > 0 && (
          <Space>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRefreshStatus}
              loading={fetchStatus}
              disabled={loading}
            >
              Check Status
            </Button>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={refreshUtxos}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        )
      }
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background:
              "linear-gradient(90deg, var(--accent) 0%, var(--accent-hover) 50%, var(--accent) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s ease-in-out infinite",
          }}
        />
      )}

      <div style={{ textAlign: "left", marginTop: 10 }}>
        <div style={{ fontWeight: "bold", marginBottom: 8 }}>Bitcoin Address:</div>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              isUserModifiedRef.current = true;
              clearError();
            }}
            placeholder="Enter Bitcoin address (e.g., bc1...)"
            style={{ fontFamily: "monospace" }}
            onPressEnter={handleFetchUtxos}
            disabled={loading}
            allowClear
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleFetchUtxos}
            loading={loading}
            disabled={!address.trim() || loading}
          >
            Load UTXOs
          </Button>
        </Space.Compact>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 10, textAlign: "left" }}
          closable
          onClose={clearError}
        />
      )}

      {loading && (
        <div
          style={{
            marginTop: 20,
            padding: 24,
            textAlign: "center",
            border: `2px solid var(--accent)`,
            borderRadius: "12px",
            backgroundColor: "rgba(255, 125, 71, 0.08)",
            boxShadow: "inset 0 2px 4px rgba(255, 125, 71, 0.1)",
          }}
        >
          <Spin size="large" style={{ color: "var(--accent)" }} />
          <div
            style={{
              marginTop: 12,
              color: "var(--accent)",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            Loading UTXOs...
          </div>
          <div
            style={{
              marginTop: 8,
              color: "var(--text-secondary)",
              fontSize: "12px",
            }}
          >
            Fetching data from sdk.txspam.lol
          </div>
        </div>
      )}

      {!loading && utxos.length > 0 && (
        <div style={{ marginTop: 20, textAlign: "left" }}>
          {/* UTXOs avec Inscriptions */}
          {inscriptionUtxos.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CheckCircleOutlined style={{ color: "#722ed1" }} />
                UTXOs with Inscriptions ({inscriptionUtxos.length}):
              </div>
              <div>
                {inscriptionUtxos.map((utxo, idx) =>
                  renderUtxoItem(utxo, idx, true)
                )}
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px",
                    backgroundColor: "rgba(114, 46, 209, 0.1)",
                    borderRadius: "6px",
                    fontWeight: "bold",
                  }}
                >
                  Total: {totalInscriptions.toLocaleString()} sats (
                  {satoshisToAmount(totalInscriptions)} BTC)
                </div>
              </div>
            </div>
          )}

          {/* UTXOs sans Inscriptions (Available Balance) */}
          {nonInscriptionUtxos.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                Available Balance (Non-Inscription UTXOs) ({nonInscriptionUtxos.length}):
              </div>
              <div>
                {nonInscriptionUtxos.map((utxo, idx) =>
                  renderUtxoItem(utxo, idx, true)
                )}
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px",
                    backgroundColor: "rgba(82, 196, 26, 0.1)",
                    borderRadius: "6px",
                    fontWeight: "bold",
                  }}
                >
                  Total: {totalNonInscriptions.toLocaleString()} sats (
                  {satoshisToAmount(totalNonInscriptions)} BTC)
                </div>
              </div>
            </div>
          )}

          {/* Unknown Status UTXOs */}
          {unknownStatusUtxos.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Tag color="default">?</Tag>
                Unknown Status:
                <Button
                  size="small"
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshStatus}
                  loading={fetchStatus}
                  style={{ padding: 0, height: "auto" }}
                >
                  Check Status
                </Button>
              </div>
              <div>
                {unknownStatusUtxos.map((utxo, idx) =>
                  renderUtxoItem(utxo, idx, true)
                )}
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px",
                    backgroundColor: "rgba(140, 140, 140, 0.1)",
                    borderRadius: "6px",
                    fontWeight: "bold",
                  }}
                >
                  Total: {totalUnknown.toLocaleString()} sats (
                  {satoshisToAmount(totalUnknown)} BTC)
                </div>
              </div>
            </div>
          )}

          {/* Market-locked UTXOs */}
          {lockedUtxos.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CloseCircleOutlined style={{ color: "#ff9800" }} />
                Market-locked UTXOs:
              </div>
              <div>
                {lockedUtxos.map((utxo, idx) =>
                  renderUtxoItem(utxo, idx, true)
                )}
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px",
                    backgroundColor: "rgba(255, 193, 7, 0.1)",
                    borderRadius: "6px",
                    fontWeight: "bold",
                  }}
                >
                  Total: {totalLocked.toLocaleString()} sats (
                  {satoshisToAmount(totalLocked)} BTC)
                </div>
              </div>
            </div>
          )}

          {/* Spent UTXOs */}
          {spentUtxos.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                Spent UTXOs:
              </div>
              <div>
                {spentUtxos.map((utxo, idx) =>
                  renderUtxoItem(utxo, idx, true)
                )}
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px",
                    backgroundColor: "rgba(255, 77, 79, 0.1)",
                    borderRadius: "6px",
                    fontWeight: "bold",
                  }}
                >
                  Total: {totalSpent.toLocaleString()} sats (
                  {satoshisToAmount(totalSpent)} BTC)
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && utxos.length === 0 && !error && address && (
        <div
          style={{
            marginTop: 20,
            textAlign: "center",
            color: "var(--text-secondary)",
          }}
        >
          Enter an address and click "Load UTXOs" to fetch the UTXO list
        </div>
      )}
    </Card>
  );
}
